import React, { useState } from 'react';
import axios from 'axios';
import { Download, ExternalLink, Loader2, ZoomIn } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { imageUrl } from '../lib/catalog';

export const TechnicalSheetViewer = ({ details, sku }) => {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const download = async () => {
    setDownloading(true);
    setError('');
    try {
      const result = await axios.get(imageUrl(details.sheetImage), { responseType: 'blob' });
      const url = URL.createObjectURL(result.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `MAXLIGHT-${sku.replace(/[^a-zA-Z0-9-]/g, '-')}-tehnicki-list.webp`;
      link.setAttribute('data-testid', 'technical-file-download');
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError('Preuzimanje nije uspjelo. Pokušajte ponovo.');
    } finally {
      setDownloading(false);
    }
  };
  return <div className="min-w-0" data-testid="technical-sheet-section">
    <h2 className="text-base md:text-lg font-semibold mb-5" data-testid="technical-sheet-heading">Tehnički list i mjere</h2>
    <button type="button" onClick={() => setOpen(true)}
      className="block w-full bg-white border border-gray-200 p-2 hover:border-gray-500 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
      aria-label={`Povećaj tehnički list ${sku}`} data-testid="technical-sheet-zoom">
      <img src={imageUrl(details.sheetImage)} alt={`Izvorni tehnički list MAXLIGHT ${sku}, stranica ${details.printedPage}`}
        className="w-full aspect-[3/4] max-h-[36rem] object-contain" loading="lazy" data-testid="technical-sheet-image" />
      <span className="flex items-center justify-center gap-2 py-3 text-sm"><ZoomIn size={17} aria-hidden="true" />Povećaj</span>
    </button>
    <p className="mt-3 text-xs text-gray-500 leading-relaxed" data-testid="technical-sheet-caption">
      {details.sourceName} · str. {details.printedPage} · šifra {details.catalogCode}
      {details.sharedSheet && <span className="block mt-1" data-testid="technical-shared-sheet-note">Izvorni list sadrži više varijanti.</span>}
    </p>
    <div className="flex flex-wrap gap-3 mt-4">
      <Button variant="outline" onClick={download} disabled={downloading} data-testid="technical-sheet-download">
        {downloading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}Preuzmi list
      </Button>
      <Button variant="ghost" asChild><a href={details.sourceUrl} target="_blank" rel="noopener noreferrer" data-testid="technical-source-link">
        <ExternalLink size={16} className="mr-2" />Izvorni katalog
      </a></Button>
    </div>
    {error && <p role="alert" className="mt-3 text-sm text-red-700" data-testid="technical-download-error">{error}</p>}
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-4xl max-h-[90dvh] overflow-y-auto p-4 sm:p-6"
        data-testid="technical-sheet-modal" closeTestId="technical-sheet-close" closeLabel="Zatvori">
        <DialogHeader className="pr-8 text-left">
          <DialogTitle data-testid="technical-sheet-modal-title">MAXLIGHT · {sku}</DialogTitle>
          <DialogDescription data-testid="technical-sheet-modal-source">{details.sourceName} · str. {details.printedPage}</DialogDescription>
        </DialogHeader>
        <img src={imageUrl(details.sheetImage)} alt={`Tehnički podaci i crteži za ${sku}`} className="block w-full h-auto" data-testid="technical-sheet-full-image" />
      </DialogContent>
    </Dialog>
  </div>;
};